"use client";

/**
 * Form field reveal (Venetian recipe 8).
 *
 * Each direct child (field row, textarea, submit button) clip-path wipes
 * in from the left while settling from scale 1.1 to 1 and fading 0 to 1:
 * duration 1, power1.out, stagger 0.15, trigger "top 80%", plays once.
 *
 * Usage: <FormReveal><div>field</div><div>field</div>...</FormReveal>
 * (each direct child animates as one unit). Transient styles are cleared
 * on complete so focus rings and dropdowns are never clipped afterwards.
 * No-JS and reduced-motion clients see the form static and complete.
 */
import { useRef, type HTMLAttributes } from "react";
import { gsap, useGSAP, prefersReducedMotion } from "./gsap";

export type FormRevealProps = HTMLAttributes<HTMLDivElement>;

export function FormReveal({
  children,
  className = "",
  ...rest
}: FormRevealProps) {
  const ref = useRef<HTMLDivElement>(null);

  useGSAP(
    () => {
      const el = ref.current;
      if (!el || prefersReducedMotion()) return;
      const fields = Array.from(el.children) as HTMLElement[];
      if (!fields.length) return;
      gsap.fromTo(
        fields,
        { clipPath: "inset(0% 100% 0% 0%)", scale: 1.1, opacity: 0 },
        {
          clipPath: "inset(0% 0% 0% 0%)",
          scale: 1,
          opacity: 1,
          duration: 1,
          ease: "power1.out",
          stagger: 0.15,
          clearProps: "clipPath,scale,opacity",
          scrollTrigger: {
            trigger: el,
            start: "top 80%",
            once: true,
          },
        }
      );
    },
    { scope: ref }
  );

  return (
    <div ref={ref} className={className} {...rest}>
      {children}
    </div>
  );
}
